import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mic, Square, Trash2 } from "lucide-react";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/notes`;

/** Record with the microphone → upload to the note → kept as audio + transcribed (whisper-1). */
export const VoiceRecorder = ({ note, onUpdated }) => {
  const [rec, setRec] = useState(false);
  const [busy, setBusy] = useState(false);
  const [secs, setSecs] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const mr = useRef(null), chunks = useRef([]), timer = useRef(null);
  useEffect(() => {
    let url;
    if (note?.has_audio) axios.get(`${API}/${note.id}/audio`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setAudioUrl(url); }).catch(() => {});
    else setAudioUrl(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [note?.id, note?.has_audio, note?.updated_at]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder?.isTypeSupported(m)) || "";
      const m = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      m.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      m.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); clearInterval(timer.current);
        const blob = new Blob(chunks.current, { type: m.mimeType || "audio/webm" });
        setBusy(true);
        try {
          const fd = new FormData(); fd.append("file", blob, "note." + ((m.mimeType || "audio/webm").includes("mp4") ? "mp4" : (m.mimeType || "").includes("ogg") ? "ogg" : "webm"));
          const r = (await axios.post(`${API}/${note.id}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
          toast.success(r.transcript_status === "done" ? "Recording saved and converted to text" : `Recording saved — transcription ${r.transcript_status}`);
          onUpdated?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not save the recording"); }
        finally { setBusy(false); }
      };
      m.start(); mr.current = m; setRec(true); setSecs(0);
      timer.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch (e) { toast.error("Microphone not available: " + (e.message || e)); }
  };
  const stop = () => { mr.current?.stop(); setRec(false); };
  const remove = async () => {
    if (!window.confirm("Delete the recording and its text?")) return;
    try { await axios.delete(`${API}/${note.id}/audio`); onUpdated?.(); } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  if (!note?.id) return <div className="text-xs text-zinc-500">Save the note first, then record.</div>;
  return (
    <div className="space-y-2" data-testid="note-voice">
      <div className="flex items-center gap-2">
        {!rec ? <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700" disabled={busy} onClick={start} data-testid="note-voice-record">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mic className="w-3.5 h-3.5 mr-1" />} {note.has_audio ? "Re-record" : "Record"}</Button>
          : <Button size="sm" variant="outline" className="h-8 border-red-400 text-red-700" onClick={stop} data-testid="note-voice-stop"><Square className="w-3.5 h-3.5 mr-1" /> Stop · {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</Button>}
        {rec && <span className="flex items-center gap-1 text-xs text-red-700"><span className="h-2 w-2 rounded-full bg-red-600 animate-pulse" /> recording…</span>}
        {busy && <span className="text-xs text-zinc-500">saving &amp; converting to text…</span>}
        {note.has_audio && !rec && <button type="button" onClick={remove} className="ml-auto text-xs text-zinc-500 hover:text-red-700 flex items-center gap-1" data-testid="note-voice-delete"><Trash2 className="w-3 h-3" /> delete recording</button>}
      </div>
      {audioUrl && <audio controls src={audioUrl} className="w-full h-9" data-testid="note-voice-player" />}
      {note.has_audio && (
        <div className="rounded border bg-zinc-50 p-2 text-xs" data-testid="note-voice-transcript">
          <div className="text-[11px] uppercase text-zinc-500 mb-0.5">Converted text {note.transcript_status && note.transcript_status !== "done" ? <span className="text-amber-700 normal-case">— {note.transcript_status}</span> : ""}</div>
          <div className="whitespace-pre-wrap">{note.transcript || <span className="text-zinc-400">no text</span>}</div>
        </div>
      )}
    </div>
  );
};
